import { sql } from "drizzle-orm";
import {
  boolean,
  check,
  date,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";
import { organisations, practiceTodos, resources, students, users } from "./schema.js";

const id = () =>
  varchar("id")
    .primaryKey()
    .default(sql`gen_random_uuid()`);

const organisationId = () =>
  varchar("organisation_id")
    .notNull()
    .references(() => organisations.id, { onDelete: "restrict" });

const createdAt = () => timestamp("created_at", { withTimezone: true }).notNull().defaultNow();

export const repertoireStatusEnum = pgEnum("repertoire_status", [
  "learning",
  "polishing",
  "performance_ready",
  "completed",
  "paused",
]);

export type RepertoireStatus = (typeof repertoireStatusEnum.enumValues)[number];


/**
 * One organisation-level repertoire item that can be reused for many students.
 *
 * Student repertoire rows keep their own status/dates/teaching note, while this
 * record is the shared teaching-library identity used for search and reusable
 * material.
 */
export const repertoireLibrary = pgTable(
  "repertoire_library",
  {
    id: id(),
    organisationId: organisationId(),
    title: text("title").notNull(),
    composer: text("composer"),
    workOrCollection: text("work_or_collection"),
    instrument: text("instrument"),
    genre: text("genre"),
    difficulty: text("difficulty"),
    tempoBpm: integer("tempo_bpm"),
    visibility: text("visibility").$type<"shared" | "private">().notNull().default("shared"),
    tags: text("tags").array().notNull().default(sql`'{}'::text[]`),
    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    archivedAt: timestamp("archived_at", { withTimezone: true }),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("repertoire_library_org_title_idx").on(t.organisationId, t.title),
    index("repertoire_library_org_updated_idx").on(t.organisationId, t.updatedAt),
    index("repertoire_library_org_visibility_idx").on(t.organisationId, t.visibility, t.createdByUserId),
    check("repertoire_library_visibility_check", sql`${t.visibility} in ('shared', 'private')`),
    check("repertoire_library_tempo_check", sql`${t.tempoBpm} is null or ${t.tempoBpm} between 1 and 400`),
  ],
);

/**
 * A durable Hub-native record of music a student is learning or has learned.
 *
 * Repertoire deliberately does not point at booking/provider data. It belongs
 * to the teaching record, survives tutor changes and may contain the same title
 * more than once when a student revisits a work years later.
 */
export const studentRepertoire = pgTable(
  "student_repertoire",
  {
    id: id(),
    organisationId: organisationId(),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    /** Optional link to the organisation-level teaching-library piece. */
    libraryPieceId: varchar("library_piece_id").references(() => repertoireLibrary.id, {
      onDelete: "set null",
    }),
    title: text("title").notNull(),
    composer: text("composer"),
    workOrCollection: text("work_or_collection"),
    instrument: text("instrument"),
    status: repertoireStatusEnum("status").notNull().default("learning"),
    startedOn: date("started_on"),
    completedOn: date("completed_on"),
    /** Staff-only teaching note. Family reads never select this field. */
    tutorNote: text("tutor_note"),
    familyVisible: boolean("family_visible").notNull().default(true),
    createdByUserId: varchar("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    /** Soft archive keeps task/resource history resolvable without destructive deletion. */
    archivedAt: timestamp("archived_at", { withTimezone: true }),
    createdAt: createdAt(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("student_repertoire_student_status_idx").on(
      t.organisationId,
      t.studentId,
      t.status,
      t.updatedAt,
    ),
    index("student_repertoire_student_started_idx").on(
      t.organisationId,
      t.studentId,
      t.startedOn,
    ),
    index("student_repertoire_library_piece_idx").on(
      t.organisationId,
      t.libraryPieceId,
      t.updatedAt,
    ),
    index("student_repertoire_completed_history_idx").on(
      t.organisationId,
      t.studentId,
      t.status,
      t.completedOn,
      t.updatedAt,
    ),
    check(
      "student_repertoire_completed_date",
      sql`(${t.status} = 'completed' and ${t.completedOn} is not null) or (${t.status} <> 'completed' and ${t.completedOn} is null)`,
    ),
    check(
      "student_repertoire_date_order",
      sql`${t.completedOn} is null or ${t.startedOn} is null or ${t.completedOn} >= ${t.startedOn}`,
    ),
  ],
);

/**
 * Optional practice-task link kept separate from the legacy-compatible to-do row.
 * The task still owns its text, so changing or archiving repertoire never rewrites
 * practice history. A hard repertoire delete is reserved for parent-record cleanup;
 * dropping the relation leaves the task text intact.
 */
export const practiceTodoRepertoireLinks = pgTable(
  "practice_todo_repertoire_links",
  {
    id: id(),
    organisationId: organisationId(),
    practiceTodoId: varchar("practice_todo_id")
      .notNull()
      .references(() => practiceTodos.id, { onDelete: "cascade" }),
    repertoireId: varchar("repertoire_id")
      .notNull()
      .references(() => studentRepertoire.id, { onDelete: "cascade" }),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("practice_todo_repertoire_todo_idx").on(t.practiceTodoId),
    index("practice_todo_repertoire_piece_idx").on(t.organisationId, t.repertoireId),
  ],
);

/**
 * Resource-to-repertoire relation for the resource workspace. A resource remains
 * one stored object/link and can be reused across pieces without copying its file.
 * Hard deletion only removes the relation; the resource row/file is owned elsewhere.
 */
export const resourceRepertoireLinks = pgTable(
  "resource_repertoire_links",
  {
    id: id(),
    organisationId: organisationId(),
    resourceId: varchar("resource_id")
      .notNull()
      .references(() => resources.id, { onDelete: "cascade" }),
    repertoireId: varchar("repertoire_id")
      .notNull()
      .references(() => studentRepertoire.id, { onDelete: "cascade" }),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("resource_repertoire_pair_idx").on(t.resourceId, t.repertoireId),
    index("resource_repertoire_piece_idx").on(t.organisationId, t.repertoireId),
  ],
);

/**
 * Organisation-level teaching material attached to a master repertoire item.
 * The resource row may be shared by several pieces without copying its bytes.
 */
export const resourceRepertoireLibraryLinks = pgTable(
  "resource_repertoire_library_links",
  {
    id: id(),
    organisationId: organisationId(),
    resourceId: varchar("resource_id")
      .notNull()
      .references(() => resources.id, { onDelete: "cascade" }),
    libraryPieceId: varchar("library_piece_id")
      .notNull()
      .references(() => repertoireLibrary.id, { onDelete: "cascade" }),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("resource_repertoire_library_pair_idx").on(t.resourceId, t.libraryPieceId),
    index("resource_repertoire_library_piece_idx").on(t.organisationId, t.libraryPieceId),
  ],
);

/**
 * A master repertoire resource is visible to a particular student only when
 * the tutor explicitly shares it against that student's repertoire row.
 */
export const studentRepertoireResourceShares = pgTable(
  "student_repertoire_resource_shares",
  {
    id: id(),
    organisationId: organisationId(),
    studentRepertoireId: varchar("student_repertoire_id")
      .notNull()
      .references(() => studentRepertoire.id, { onDelete: "cascade" }),
    resourceId: varchar("resource_id")
      .notNull()
      .references(() => resources.id, { onDelete: "cascade" }),
    sharedByUserId: varchar("shared_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    /**
     * Frozen teaching content for Hub-native assets.
     *
     * Files and links leave this null. A Groove resource stores the title/query
     * that existed when this student was given it, so editing the reusable
     * master later cannot rewrite the student's material underneath them.
     * Future Hub providers can add their own provider-tagged payload without
     * widening this table for every new teaching tool.
     */
    hubSnapshot: jsonb("hub_snapshot").$type<Record<string, unknown>>(),
    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex("student_repertoire_resource_share_pair_idx").on(
      t.studentRepertoireId,
      t.resourceId,
    ),
    index("student_repertoire_resource_share_piece_idx").on(
      t.organisationId,
      t.studentRepertoireId,
    ),
  ],
);

